import { analyticsService } from "@services/analyticsService";
import { WSAlert, WSElement } from "@wingspanhq/fe-component-library";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { useHistory } from "react-router-dom";
import { useQueryTaxEstimate } from "@hooks/useQueryTaxEstimate";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { useNudgeDismissal } from "@hooks/useNudgeDismissal";
import { useOnVisible } from "../../../shared/hooks/useOnVisible";
import { selectorTaxRateLabel } from "../../../shared/selectors/selectorTaxRateLabel";
import { PATH_ONBOARDING_TAX_WITHHOLDINGS } from "../../Onboarding";
import { useModalDismissTaxWithholdingsSetup } from "../modals/ModalDismissTaxWithholdingsSetup";
import { useModalPauseReminders } from "../modals/ModalPauseReminders";
import { useProposeTaxWithholdings } from "../hooks/useProposeTaxWithholdings";

type Props = {
  invoiceNetAmount?: number;
  invoiceCurrency?: Currency;
};

export const AlertEstimatedTaxWithholdings: React.FC<Props> = props => {
  const history = useHistory();
  const queryFeatureFlags = useQueryFeatureFlags();
  const { propose, hasSetRate } = useProposeTaxWithholdings();
  const [dismissed] = useNudgeDismissal("taxWithholdingsAlerts");

  const modalDismissTaxWithholdingsSetup =
    useModalDismissTaxWithholdingsSetup();
  const modalPauseReminders = useModalPauseReminders();

  const handleDismiss = () => {
    modalDismissTaxWithholdingsSetup.open({
      onPauseReminders: () => {
        modalPauseReminders.open();
      }
    });
  };

  const showTaxWithholdingAlert =
    !!queryFeatureFlags.data?.taxWithholdingInvoicingAlerts &&
    !dismissed &&
    // The inverse of AlertEnableTaxWithholdings: this variant quotes a rate, so
    // it only applies once the member has saved one.
    hasSetRate &&
    propose;

  const queryTaxEstimate = useQueryTaxEstimate(new Date().getFullYear(), {
    enabled: showTaxWithholdingAlert
  });

  const [ref] = useOnVisible(() => {
    analyticsService.track("Invoicing Tax Withholdings Alert Viewed", {
      variant: "estimated_tax_rate"
    });
  });

  if (!showTaxWithholdingAlert) {
    return null;
  }

  const esitimatedTaxRate = queryTaxEstimate.data?.effectiveTaxRate || 0;

  if (esitimatedTaxRate === 0) {
    return null;
  }

  const recommendedInvoiceAmount = props.invoiceNetAmount
    ? (props.invoiceNetAmount * esitimatedTaxRate) / 100
    : undefined;

  return (
    <WSAlert
      theme="warning"
      icon="alert"
      title="Stay on track for taxes"
      action={{
        text: "Complete your setup",
        onClick: () => {
          analyticsService.track("Invoicing Tax Withholdings Alert Click", {
            variant: "estimated_tax_rate"
          });
          history.push(PATH_ONBOARDING_TAX_WITHHOLDINGS);
        }
      }}
      onDismiss={handleDismiss}
    >
      <WSElement ref={ref}></WSElement>
      {recommendedInvoiceAmount && props.invoiceCurrency ? (
        <>
          Based on your tax profile, we recommend setting aside{" "}
          {formatMoney(recommendedInvoiceAmount, props.invoiceCurrency)} from
          this invoice to cover the taxes you'll owe as an independent
          contractor. Taking action now helps you stay on track—no scrambling
          later.
        </>
      ) : (
        <>
          Based on your tax profile, we recommend setting aside{" "}
          {selectorTaxRateLabel(esitimatedTaxRate)} from your earnings to cover
          the taxes you’ll owe as an independent contractor. Taking action now
          helps you stay on track—no scrambling later.
        </>
      )}
    </WSAlert>
  );
};
